// Curated SVG icon library for LOGOMAN PRO.
// Each entry is a viewBox 0 0 100 100 path string.
// Designed to be tiny + tree-shakeable. Add more freely.

/**
 * Path entry for an icon. Plain strings stay single-color (rendered with
 * `iconColor`). Objects allow per-path fill so faces can mix black eyes,
 * accent cheeks, etc.
 *
 * `fill` values:
 *   - "primary"  → iconColor (default)
 *   - "accent"   → iconAccent
 *   - "#xxxxxx"  → literal color (e.g. always-black eyes, always-white highlights)
 */
export type IconPath =
  | string
  | { d: string; fill?: "primary" | "accent" | string; fillRule?: "nonzero" | "evenodd" };

export interface IconDef {
  id: string;
  name: string;
  category: "abstract" | "tech" | "nature" | "object" | "letter" | "face" | "cartoon";
  paths: IconPath[];
  /** Default fill-rule applied to string paths. Object paths can override per-entry. */
  fillRule?: "nonzero" | "evenodd";
  /** True when at least one path uses `fill: "accent"` — drives the accent picker UI. */
  usesAccent?: boolean;
  /**
   * For raster cartoon icons: URL to a PNG (e.g. "/cartoons/page-1.png").
   * When set, the renderer emits an SVG <image> element instead of paths.
   * Patterns + per-path colors don't apply to raster icons.
   */
  pngUrl?: string;
}

export const ICONS: IconDef[] = [
  { id: "circle",    name: "Circle",    category: "abstract", paths: ["M50 10 A40 40 0 1 0 50 90 A40 40 0 1 0 50 10 Z"] },
  { id: "square",    name: "Square",    category: "abstract", paths: ["M15 15 H85 V85 H15 Z"] },
  { id: "triangle",  name: "Triangle",  category: "abstract", paths: ["M50 12 L88 82 H12 Z"] },
  { id: "diamond",   name: "Diamond",   category: "abstract", paths: ["M50 10 L90 50 L50 90 L10 50 Z"] },
  { id: "hex",       name: "Hexagon",   category: "abstract", paths: ["M50 8 L86 28 L86 72 L50 92 L14 72 L14 28 Z"] },
  { id: "star",      name: "Star",      category: "abstract", paths: ["M50 10 L61 38 L91 38 L67 56 L76 84 L50 67 L24 84 L33 56 L9 38 L39 38 Z"] },
  { id: "bolt",      name: "Bolt",      category: "abstract", paths: ["M55 10 L20 55 L45 55 L40 90 L78 42 L52 42 Z"] },
  { id: "infinity",  name: "Infinity",  category: "abstract", paths: ["M30 35 C15 35 15 65 30 65 C45 65 55 35 70 35 C85 35 85 65 70 65 C55 65 45 35 30 35 Z"] },
  { id: "heart",     name: "Heart",     category: "abstract", paths: ["M50 85 L20 55 C10 45 15 25 30 25 C40 25 45 32 50 40 C55 32 60 25 70 25 C85 25 90 45 80 55 Z"] },
  { id: "leaf",      name: "Leaf",      category: "nature",   paths: ["M20 80 C20 40 40 20 80 20 C80 60 60 80 20 80 Z"] },
  { id: "drop",      name: "Drop",      category: "nature",   paths: ["M50 10 C30 40 22 55 22 70 A28 28 0 0 0 78 70 C78 55 70 40 50 10 Z"] },
  { id: "tree",      name: "Tree",      category: "nature",   paths: ["M50 10 L70 40 L62 40 L82 65 L70 65 L88 90 L12 90 L30 65 L18 65 L38 40 L30 40 Z"] },
  { id: "eye",       name: "Eye",       category: "object",   paths: ["M10 50 C25 25 75 25 90 50 C75 75 25 75 10 50 Z", "M50 35 A15 15 0 1 0 50 65 A15 15 0 1 0 50 35 Z"] },
  { id: "globe",     name: "Globe",     category: "tech",     paths: ["M50 10 A40 40 0 1 0 50 90 A40 40 0 1 0 50 10 Z", "M10 50 H90", "M50 10 C30 30 30 70 50 90 M50 10 C70 30 70 70 50 90"] },
  { id: "spark",     name: "Spark",     category: "tech",     paths: ["M50 10 L55 45 L90 50 L55 55 L50 90 L45 55 L10 50 L45 45 Z"] },
  { id: "shield",    name: "Shield",    category: "object",   paths: ["M50 10 L85 22 V52 C85 72 70 86 50 92 C30 86 15 72 15 52 V22 Z"] },
  { id: "flame",     name: "Flame",     category: "abstract", paths: ["M50 10 C30 35 30 50 40 60 C30 60 30 75 50 90 C70 75 70 60 60 60 C70 50 70 35 50 10 Z"] },
  { id: "wave",      name: "Wave",      category: "abstract", paths: ["M10 60 C25 40 40 80 55 60 C70 40 85 80 90 60"] },
  { id: "ring",      name: "Ring",      category: "abstract", paths: ["M50 15 A35 35 0 1 0 50 85 A35 35 0 1 0 50 15 Z M50 30 A20 20 0 1 1 50 70 A20 20 0 1 1 50 30 Z"] },
  { id: "moon",      name: "Moon",      category: "nature",   paths: ["M65 12 C45 12 28 28 28 50 C28 72 45 88 65 88 C50 80 42 65 42 50 C42 35 50 20 65 12 Z"] },

  // ===== Faces / characters =====
  // All face icons render as a single compound path with fill-rule="evenodd"
  // so eyes/mouth/details show the background through the head silhouette.
  {
    id: "smile", name: "Smile", category: "face", fillRule: "evenodd",
    paths: ["M50 12 A38 38 0 1 0 50 88 A38 38 0 1 0 50 12 Z M33 42 a5 5 0 1 0 10 0 a5 5 0 1 0 -10 0 Z M57 42 a5 5 0 1 0 10 0 a5 5 0 1 0 -10 0 Z M32 58 Q50 75 68 58 L68 64 Q50 81 32 64 Z"],
  },
  {
    id: "happy-face", name: "Happy", category: "face", fillRule: "evenodd",
    paths: ["M50 10 A40 40 0 1 0 50 90 A40 40 0 1 0 50 10 Z M30 38 Q35 30 40 38 Z M60 38 Q65 30 70 38 Z M28 56 Q50 80 72 56 Q50 70 28 56 Z"],
  },
  {
    id: "kid-face", name: "Kid", category: "face", fillRule: "evenodd",
    paths: ["M50 18 A35 35 0 1 0 50 88 A35 35 0 1 0 50 18 Z M44 12 Q47 2 50 10 Q53 2 56 12 Q53 16 50 14 Q47 16 44 12 Z M37 44 a4 4 0 1 0 8 0 a4 4 0 1 0 -8 0 Z M55 44 a4 4 0 1 0 8 0 a4 4 0 1 0 -8 0 Z M34 60 Q50 75 66 60 L66 66 Q50 81 34 66 Z"],
  },
  {
    id: "girl-face", name: "Girl", category: "face", fillRule: "evenodd",
    paths: [
      // Head + bangs (the bangs come down from the top, head is below the hairline)
      "M50 16 A36 36 0 1 0 50 88 A36 36 0 1 0 50 16 Z " +
      // Hair bow on top
      "M40 12 Q35 4 42 4 Q48 4 48 10 Q52 10 52 4 Q60 4 60 12 Q60 16 50 14 Q40 16 40 12 Z " +
      // Eyes
      "M37 44 a4 4 0 1 0 8 0 a4 4 0 1 0 -8 0 Z " +
      "M55 44 a4 4 0 1 0 8 0 a4 4 0 1 0 -8 0 Z " +
      // Smile
      "M36 60 Q50 73 64 60 L64 65 Q50 78 36 65 Z",
    ],
  },
  {
    id: "cat-face", name: "Cat", category: "face", fillRule: "evenodd",
    paths: [
      "M50 30 A28 28 0 1 0 50 88 A28 28 0 1 0 50 30 Z " +     // head
      "M26 36 L36 16 L44 38 Z " +                              // left ear
      "M56 38 L64 16 L74 36 Z " +                              // right ear
      "M36 50 Q40 44 44 50 Q40 54 36 50 Z " +                  // closed left eye ^^
      "M56 50 Q60 44 64 50 Q60 54 56 50 Z " +                  // closed right eye
      "M48 60 L52 60 L50 64 Z " +                              // nose
      "M40 68 Q50 78 60 68 L60 72 Q50 82 40 72 Z",             // smile
    ],
  },
  {
    id: "dog-face", name: "Dog", category: "face", fillRule: "evenodd",
    paths: [
      "M50 32 A26 26 0 1 0 50 86 A26 26 0 1 0 50 32 Z " +      // head
      "M22 36 Q14 52 22 66 Q32 66 32 50 Q32 36 22 36 Z " +     // left floppy ear
      "M78 36 Q86 52 78 66 Q68 66 68 50 Q68 36 78 36 Z " +     // right floppy ear
      "M38 52 a3 3 0 1 0 6 0 a3 3 0 1 0 -6 0 Z " +             // left eye
      "M56 52 a3 3 0 1 0 6 0 a3 3 0 1 0 -6 0 Z " +             // right eye
      "M46 62 L54 62 L50 68 Z " +                              // nose
      "M42 72 Q50 80 58 72 L58 76 Q50 84 42 76 Z",             // smile
    ],
  },
  {
    id: "bear-face", name: "Bear", category: "face", fillRule: "evenodd",
    paths: [
      "M50 30 A28 28 0 1 0 50 88 A28 28 0 1 0 50 30 Z " +      // head
      "M26 32 a8 8 0 1 0 16 0 a8 8 0 1 0 -16 0 Z " +           // left round ear
      "M58 32 a8 8 0 1 0 16 0 a8 8 0 1 0 -16 0 Z " +           // right round ear
      "M40 50 a3 3 0 1 0 6 0 a3 3 0 1 0 -6 0 Z " +             // left eye
      "M54 50 a3 3 0 1 0 6 0 a3 3 0 1 0 -6 0 Z " +             // right eye
      "M46 62 a4 3 0 1 0 8 0 a4 3 0 1 0 -8 0 Z " +             // nose
      "M40 70 Q50 78 60 70 L60 74 Q50 82 40 74 Z",             // smile
    ],
  },
  {
    id: "ghost-face", name: "Ghost", category: "face", fillRule: "evenodd",
    paths: [
      "M50 12 A30 30 0 0 0 20 42 L20 80 Q26 90 32 80 Q40 90 46 80 Q50 88 54 80 Q60 90 68 80 Q74 90 80 80 L80 42 A30 30 0 0 0 50 12 Z " +
      "M40 42 a4 4 0 1 0 8 0 a4 4 0 1 0 -8 0 Z " +
      "M52 42 a4 4 0 1 0 8 0 a4 4 0 1 0 -8 0 Z " +
      "M46 60 a3 4 0 1 0 6 0 a3 4 0 1 0 -6 0 Z",
    ],
  },
  {
    id: "robot-face", name: "Robot", category: "face", fillRule: "evenodd",
    paths: [
      "M48 8 L52 8 L52 18 L48 18 Z " +                         // antenna stalk
      "M46 4 a4 4 0 1 0 8 0 a4 4 0 1 0 -8 0 Z " +              // antenna tip
      "M16 22 H84 V82 H16 Z " +                                // head box
      "M28 38 H44 V52 H28 Z " +                                // left eye rect
      "M56 38 H72 V52 H56 Z " +                                // right eye rect
      "M30 64 H70 V70 H30 Z",                                  // mouth bar
    ],
  },
  {
    id: "minimal-smile", name: "Minimal smile", category: "face",
    // Just features — two oval eyes with white "shine" highlights and a banana smile.
    // No head silhouette, so this works floating over any container (cupcake, cloud, phone).
    paths: [
      // Left + right eye ovals (use primary so user can recolor)
      { d: "M27 32 a8 11 0 1 0 16 0 a8 11 0 1 0 -16 0 Z", fill: "primary" },
      { d: "M57 32 a8 11 0 1 0 16 0 a8 11 0 1 0 -16 0 Z", fill: "primary" },
      // Tiny white highlight dots at the top-right of each eye
      { d: "M37 27 a2.5 2.5 0 1 0 5 0 a2.5 2.5 0 1 0 -5 0 Z", fill: "#ffffff" },
      { d: "M67 27 a2.5 2.5 0 1 0 5 0 a2.5 2.5 0 1 0 -5 0 Z", fill: "#ffffff" },
      // Simple banana smile
      { d: "M30 58 Q50 78 70 58 L68 64 Q50 82 32 64 Z", fill: "primary" },
    ],
  },
  {
    id: "kawaii-face", name: "Kawaii", category: "face", usesAccent: true,
    // Multi-color: head uses iconColor (primary), eyes are always black with
    // white highlights, mouth + cheeks use iconAccent (the recolourable accent).
    // Designed to match a Pikachu-style kawaii reference: big oval eyes with
    // top-right white highlight, two oval cheek blushes, jagged chevron mouth.
    paths: [
      // Head silhouette
      { d: "M50 8 A42 42 0 1 0 50 92 A42 42 0 1 0 50 8 Z", fill: "primary" },
      // Eyes (black ovals)
      { d: "M22 32 a13 16 0 1 0 26 0 a13 16 0 1 0 -26 0 Z", fill: "#0f172a" },
      { d: "M52 32 a13 16 0 1 0 26 0 a13 16 0 1 0 -26 0 Z", fill: "#0f172a" },
      // Eye highlights (white sparkle dots — top-right of each eye)
      { d: "M40 38 a3.5 3.5 0 1 0 7 0 a3.5 3.5 0 1 0 -7 0 Z", fill: "#ffffff" },
      { d: "M70 38 a3.5 3.5 0 1 0 7 0 a3.5 3.5 0 1 0 -7 0 Z", fill: "#ffffff" },
      // Cheek blushes (accent color)
      { d: "M8 64 a8 4 0 1 0 16 0 a8 4 0 1 0 -16 0 Z", fill: "accent" },
      { d: "M76 64 a8 4 0 1 0 16 0 a8 4 0 1 0 -16 0 Z", fill: "accent" },
      // Mouth — chevron/zigzag kawaii smile (accent fill)
      { d: "M40 62 L44 66 L50 62 L56 66 L60 62 L60 74 L56 78 L50 74 L44 78 L40 74 Z", fill: "accent" },
    ],
  },
  {
    id: "sun-face", name: "Sun", category: "face", fillRule: "evenodd",
    paths: [
      // Head circle
      "M50 30 A22 22 0 1 0 50 74 A22 22 0 1 0 50 30 Z " +
      // 8 sun rays as small triangles around the head
      "M50 4 L46 22 L54 22 Z " +                               // top
      "M96 50 L78 46 L78 54 Z " +                              // right
      "M50 96 L46 78 L54 78 Z " +                              // bottom
      "M4 50 L22 46 L22 54 Z " +                               // left
      "M82 18 L66 30 L74 38 Z " +                              // top-right
      "M82 82 L74 66 L66 70 Z " +                              // bottom-right (approx)
      "M18 82 L34 70 L26 66 Z " +                              // bottom-left
      "M18 18 L26 34 L34 26 Z " +                              // top-left
      // Eyes + smile
      "M42 48 a2 2 0 1 0 4 0 a2 2 0 1 0 -4 0 Z " +
      "M54 48 a2 2 0 1 0 4 0 a2 2 0 1 0 -4 0 Z " +
      "M42 56 Q50 64 58 56 L58 60 Q50 68 42 60 Z",
    ],
  },
];

// ===== Cartoon stickers =====
// Raster (PNG) cartoon icons — the PNGs live in /public/cartoons/ and are
// served at runtime. They render as <image> elements in the SVG, so patterns
// and per-path color treatments don't apply.
const CARTOONS: Array<{ id: string; name: string; file: string }> = [
  { id: "cartoon-corgi-bread",     name: "Corgi & bread",   file: "page-1.png"  },
  { id: "cartoon-samurai",         name: "Samurai kid",     file: "page-2.png"  },
  { id: "cartoon-turtle-melon",    name: "Turtle & melon",  file: "page-3.png"  },
  { id: "cartoon-sushi-cat",       name: "Sushi cat",       file: "page-4.png"  },
  { id: "cartoon-monkey-rice",     name: "Monkey & rice",   file: "page-5.png"  },
  { id: "cartoon-lucky-cat",       name: "Lucky cat",       file: "page-6.png"  },
  { id: "cartoon-taiko-dog",       name: "Taiko dog",       file: "page-7.png"  },
  { id: "cartoon-takoyaki",        name: "Takoyaki",        file: "page-8.png"  },
  { id: "cartoon-matcha-cup",      name: "Matcha cup",      file: "page-9.png"  },
  { id: "cartoon-onsen-chick",     name: "Onsen chick",     file: "page-10.png" },
  { id: "cartoon-star-panda",      name: "Star panda",      file: "page-11.png" },
  { id: "cartoon-owl-glasses",     name: "Owl",             file: "page-12.png" },
  { id: "cartoon-boba-tea",        name: "Boba tea",        file: "page-13.png" },
  { id: "cartoon-onigiri-glasses", name: "Onigiri",         file: "page-14.png" },
  { id: "cartoon-cat-glasses",     name: "Cat & glasses",   file: "page-15.png" },
  { id: "cartoon-ninja-kid",       name: "Ninja kid",       file: "page-16.png" },
  { id: "cartoon-bunny-glasses",   name: "Bunny",           file: "page-17.png" },
  { id: "cartoon-fox-glasses",     name: "Fox",             file: "page-18.png" },
];
for (const c of CARTOONS) {
  ICONS.push({
    id: c.id,
    name: c.name,
    category: "cartoon",
    paths: [],
    pngUrl: `/cartoons/${c.file}`,
  });
}

export const ICON_CATEGORIES = ["abstract", "tech", "nature", "object", "face", "cartoon", "letter"] as const;

export function getIcon(id: string | null): IconDef | undefined {
  if (!id) return undefined;
  return ICONS.find((i) => i.id === id);
}
